import { useEffect, useMemo, useRef, useState } from "react";
import { Maximize2, Minimize2 } from "lucide-react";
import { tokenUrl } from "@/lib/api";
import { decimalSep, t } from "@/lib/i18n";
import type {
  CodeStep,
  Device,
  Edge,
  EventLevel,
  IncidentData,
  Signal,
  SignalKind,
} from "@/lib/zeitstrahl/types";

/**
 * Störungs-Replay als Logikanalysator — React/Canvas-Port des Mockups
 * `leif-zeitstrahl.html`. Cursor ziehen scrubbt das Kamera-Video + die
 * Signalauslesung synchron; Zoom Zyklus/±3 s/±300 ms; Gutlauf-Vergleich blendet
 * Referenzspuren ein. Die imperative Zeichenlogik lebt in einem Controller in
 * `useEffect`, React ist die Hülle für Toolbar + Auslesung.
 *
 * Optik folgt dem App-Theme (hell/dunkel): der Rahmen über Tailwind-Tokens, das
 * Canvas liest die CSS-Variablen (`--card`/`--border`/…) und zeichnet bei einem
 * Theme-Wechsel neu. Vollbild-Umschalter oben rechts in der Werkzeugleiste.
 *
 * Die Kamera ist das echte, von Blackbox hochgeladene Video (per Range-Streaming
 * am Payload-Endpunkt). Beim Scrubben folgt es dem Cursor; MediaRecorder-WebM
 * ohne Dauer-Header wird per Seek „aufgeweckt". Liegt kein Video vor, Platzhalter.
 */

const FMONO = 'ui-monospace, SFMono-Regular, Menlo, "Liberation Mono", monospace';

const ROW_DEV = 32;
const ROW_SIG = 24;
const ROW_CODE = 30;
const ROW_HEAD = 22;
const LABEL_W = 200;

// ── Semantik-Akzente (DI/DO, Ereignis-Level, Cursor). Theme-abhängig, damit sie
// auf hellem wie dunklem Grund lesbar bleiben (auf Hell dunkler/gesättigter). ──
type Accents = { di: string; do: string; rot: string; amber: string; gruen: string; blau: string };
function accents(dark: boolean): Accents {
  return dark
    ? { di: "#6fb7e8", do: "#f2c078", rot: "#e2503f", amber: "#f0b13e", gruen: "#3fbe6e", blau: "#5aa9e6" }
    : { di: "#2f7fc0", do: "#b9770d", rot: "#d23a2c", amber: "#b47f0c", gruen: "#2f9451", blau: "#2e86c8" };
}

// Canvas-Palette aus den Theme-CSS-Variablen (Struktur) + Akzenten.
type CanvasTheme = Accents & {
  screen: string; head: string; grid: string; ink: string; inkDim: string; ghost: string;
};
function readCanvasTheme(): CanvasTheme {
  const cs = getComputedStyle(document.documentElement);
  const hsl = (name: string, fb: string) => {
    const raw = cs.getPropertyValue(name).trim();
    if (!raw) return fb;
    const p = raw.split(/\s+/);
    return p.length >= 3 ? `hsl(${p[0]}, ${p[1]}, ${p[2]})` : fb;
  };
  const dark = document.documentElement.classList.contains("dark");
  return {
    screen: hsl("--card", dark ? "#0f151a" : "#ffffff"),
    head: hsl("--muted", dark ? "#151e25" : "#eef2f3"),
    grid: hsl("--border", dark ? "#22303a" : "#e2e8ea"),
    ink: hsl("--foreground", dark ? "#e6edf0" : "#1a2226"),
    inkDim: hsl("--muted-foreground", dark ? "#8a9aa4" : "#64748b"),
    ghost: dark ? "#5c6b74" : "#9aa7ae",
    ...accents(dark),
  };
}

// ── Reine Hilfsfunktionen ───────────────────────────────────────────────────
function valueAt(edges: Edge[], t: number): number {
  let v = edges[0][1];
  for (let i = 0; i < edges.length; i++) {
    if (edges[i][0] <= t) v = edges[i][1];
    else break;
  }
  return v;
}
function sinceAt(edges: Edge[], t: number): number {
  let last = edges[0][0];
  for (let i = 0; i < edges.length; i++) {
    if (edges[i][0] <= t) last = edges[i][0];
    else break;
  }
  return t - last;
}
function clockStr(t0Clock: number, t: number): string {
  let s = t0Clock + t;
  if (s < 0) s += 86400;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const se = Math.floor(s % 60);
  const ms = Math.round((s - Math.floor(s)) * 1000);
  const p = (n: number, w: number) => String(n).padStart(w, "0");
  return `${p(h, 2)}:${p(m, 2)}:${p(se, 2)}${decimalSep()}${p(ms, 3)}`;
}
function relStr(t: number): string {
  const sign = t < 0 ? "−" : t > 0 ? "+" : " ";
  return "T " + sign + Math.abs(t).toFixed(3).replace(".", decimalSep()) + " s";
}
function durStr(d: number): string {
  if (d < 1) return Math.round(d * 1000) + " ms";
  return d.toFixed(2).replace(".", decimalSep()) + " s";
}
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

/** Name des Arbeitsschritts (Funktion), dessen Zeilen-Range die Zeile enthält. */
function stepAt(steps: CodeStep[], line: number): string | null {
  for (const s of steps) if (line >= s.line && line <= s.endLine) return s.name;
  return null;
}

// ── Zeilen-Layout ────────────────────────────────────────────────────────────
type Row =
  | { kind: "head"; label: string; y: number; h: number }
  | { kind: "dev"; ref: Device; y: number; h: number }
  | { kind: "sig"; ref: Signal; y: number; h: number }
  | { kind: "code"; y: number; h: number };

function buildRows(data: IncidentData): { rows: Row[]; totalH: number } {
  const rows: Row[] = [];
  let y = 0;
  if (data.code && data.code.track.length) {
    rows.push({ kind: "head", label: t("Programm · Ablauf"), y, h: ROW_HEAD });
    y += ROW_HEAD;
    rows.push({ kind: "code", y, h: ROW_CODE });
    y += ROW_CODE;
  }
  rows.push({ kind: "head", label: t("Geräte"), y, h: ROW_HEAD });
  y += ROW_HEAD;
  for (const d of data.devices) {
    rows.push({ kind: "dev", ref: d, y, h: ROW_DEV });
    y += ROW_DEV;
  }
  rows.push({ kind: "head", label: t("Signale · Eingänge / Ausgänge"), y, h: ROW_HEAD });
  y += ROW_HEAD;
  for (const s of data.signals) {
    rows.push({ kind: "sig", ref: s, y, h: ROW_SIG });
    y += ROW_SIG;
  }
  return { rows, totalH: y };
}

// ── React-State der Auslesung ────────────────────────────────────────────────
type ReadoutSignal = { id: string; pin: string; name: string; on: boolean; kind: SignalKind; since: string };
type ReadoutEvent = { lvl: EventLevel; label: string; rel: string; title: string; det: string; payload?: string };
type ReadoutCode = { step: string | null; line: number; text: string };
type Readout = { rel: string; abs: string; signals: ReadoutSignal[]; event: ReadoutEvent | null; diffNote: string | null; code: ReadoutCode | null };

type Controller = {
  setZoom: (z: number) => void;
  toggleCompare: () => void;
  togglePlay: () => void;
  exportPkg: () => void;
};

const LVL_LABEL: Record<EventLevel, string> = { err: t("Error"), warn: t("Warnung"), ok: t("OK"), info: t("Info") };

/** Debugger-Mini-Ansicht: ~9 Zeilen rund um die aktive, aktive hervorgehoben. */
function CodeView({ lines, active }: { lines: string[]; active: number }) {
  const from = Math.max(1, active - 4);
  const to = Math.min(lines.length, from + 8);
  const out = [];
  for (let n = from; n <= to; n++) {
    const isA = n === active;
    out.push(
      <div key={n} className={"flex gap-3 rounded-sm px-1 " + (isA ? "bg-primary/15" : "")}>
        <span className={"w-8 shrink-0 select-none text-right tabular-nums " + (isA ? "text-primary" : "text-muted-foreground/50")}>
          {n}
        </span>
        <span className={"truncate " + (isA ? "font-semibold text-foreground" : "text-muted-foreground")}>
          {lines[n - 1] || " "}
        </span>
      </div>,
    );
  }
  return <>{out}</>;
}

export function ZeitstrahlReplay({ data, incidentId }: { data: IncidentData; incidentId?: string }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const plotRef = useRef<HTMLCanvasElement>(null);
  const plotWrapRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const ctrlRef = useRef<Controller | null>(null);

  const { rows, totalH } = useMemo(() => buildRows(data), [data]);

  const [zoom, setZoomUi] = useState(1);
  const [compare, setCompareUi] = useState(false);
  const [playing, setPlayingUi] = useState(false);
  const [readout, setReadout] = useState<Readout | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [isFull, setIsFull] = useState(false);

  // Theme (hell/dunkel) verfolgen → Akzentfarben für die React-Hülle.
  const [dark, setDark] = useState(
    () => typeof document !== "undefined" && document.documentElement.classList.contains("dark"),
  );
  useEffect(() => {
    const el = document.documentElement;
    const obs = new MutationObserver(() => setDark(el.classList.contains("dark")));
    obs.observe(el, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  const A = useMemo(() => accents(dark), [dark]);
  const lvlColor = (lvl: EventLevel) => ({ err: A.rot, warn: A.amber, ok: A.gruen, info: A.blau })[lvl];

  // Vollbild = die Replay-Umgebung als Vollflächen-Overlay (zuverlässig in jedem
  // Einbettungskontext, anders als die Fullscreen-API; Esc schließt).
  useEffect(() => {
    if (!isFull) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsFull(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isFull]);
  const toggleFull = () => setIsFull((f) => !f);

  useEffect(() => {
    const plot = plotRef.current;
    const wrap = plotWrapRef.current;
    if (!plot || !wrap) return;
    const pctx = plot.getContext("2d");
    if (!pctx) return;

    let TH = readCanvasTheme();
    const lvlCol = (lvl: EventLevel) => ({ err: TH.rot, warn: TH.amber, ok: TH.gruen, info: TH.blau })[lvl];

    // Echtes Kamera-Video (per Range-Streaming), sonst null → Platzhalter.
    const video = data.camera ? videoRef.current : null;
    let vdur = video && Number.isFinite(video.duration) ? video.duration : 0;

    // Cursor-Zeit ↔ Video-Position, 1:1 in Echtzeit. Der Zeitversatz kommt aus
    // `startT` (explizit) oder aus `endT` − Videolänge: Blackbox-Clips sind
    // Dashcam-Ringpuffer, die beim Trigger (T0) stoppen, das Video endet also
    // bei endT (≈ 0). Das frühere Strecken des Clips über [bounds] war falsch:
    // bei einem 66-Minuten-Fenster landete jede Cursorposition auf dem
    // letzten Frame.
    const camStart = (): number | null => {
      if (data.camera && typeof data.camera.startT === "number") return data.camera.startT;
      if (!vdur) return null;
      const endT = data.camera && typeof data.camera.endT === "number" ? data.camera.endT : 0;
      return endT - vdur;
    };
    const cursorToVideoRaw = (t: number) => {
      const cs = camStart();
      return cs === null ? 0 : t - cs;
    };
    const cursorToVideo = (t: number) => clamp(cursorToVideoRaw(t), 0, vdur);
    const videoToCursor = (v: number) => (camStart() ?? 0) + v;
    // Deckt das Video den Cursorzeitpunkt ab (liegt er innerhalb der Aufnahme)?
    const videoCovers = (t: number) => {
      if (!vdur) return false;
      const v = cursorToVideoRaw(t);
      return v >= 0 && v < vdur - 0.05;
    };
    // ── veränderlicher Zustand (wie das Mockup-IIFE) ──
    let cursor = 0;
    let win: [number, number] = [...data.windows[1]] as [number, number];
    let compareL = false;
    let playing = false;
    let dragging = false;
    let raf = 0;
    let lastFrame = 0;
    let PW = 800;
    let DPR = 1;
    const PH = totalH;

    // Programm-Spur: Schritt-Segmente über die Zeit (aus track + steps).
    type CodeSeg = { t0: number; t1: number; step: string | null };
    const codeSegs: CodeSeg[] = [];
    if (data.code) {
      const tr = data.code.track;
      for (let i = 0; i < tr.length; i++) {
        const t0s = tr[i][0];
        const t1s = i + 1 < tr.length ? tr[i + 1][0] : data.bounds[1];
        const step = stepAt(data.code.steps, tr[i][1]);
        const last = codeSegs[codeSegs.length - 1];
        if (last && last.step === step) last.t1 = t1s;
        else codeSegs.push({ t0: t0s, t1: t1s, step });
      }
    }

    const xOf = (t: number) => ((t - win[0]) / (win[1] - win[0])) * PW;
    const tOf = (x: number) => win[0] + (x / PW) * (win[1] - win[0]);

    function tickStep(): number {
      const span = win[1] - win[0];
      if (span > 8) return 1;
      if (span > 3) return 0.5;
      if (span > 1) return 0.2;
      return 0.05;
    }

    function drawTrace(edges: Edge[], hi: number, lo: number, color: string, width: number, dash: number[] | null) {
      pctx!.save();
      pctx!.strokeStyle = color;
      pctx!.lineWidth = width;
      pctx!.lineJoin = "miter";
      if (dash) pctx!.setLineDash(dash);
      pctx!.beginPath();
      let y = valueAt(edges, win[0]) ? hi : lo;
      pctx!.moveTo(0, y);
      for (const e of edges) {
        if (e[0] <= win[0] || e[0] > win[1]) continue;
        const x = xOf(e[0]);
        pctx!.lineTo(x, y);
        y = e[1] ? hi : lo;
        pctx!.lineTo(x, y);
      }
      pctx!.lineTo(PW, y);
      pctx!.stroke();
      pctx!.restore();
    }

    function drawPlot() {
      pctx!.clearRect(0, 0, PW, PH);
      pctx!.fillStyle = TH.screen;
      pctx!.fillRect(0, 0, PW, PH);

      // Zeilenstreifen
      for (const r of rows) {
        if (r.kind === "head") {
          pctx!.fillStyle = TH.head;
          pctx!.fillRect(0, r.y, PW, r.h);
        }
        pctx!.strokeStyle = TH.grid;
        pctx!.lineWidth = 1;
        pctx!.beginPath();
        pctx!.moveTo(0, r.y + r.h - 0.5);
        pctx!.lineTo(PW, r.y + r.h - 0.5);
        pctx!.stroke();
      }

      // Raster
      const step = tickStep();
      const first = Math.ceil(win[0] / step) * step;
      pctx!.font = `500 10px ${FMONO}`;
      pctx!.textBaseline = "middle";
      for (let t = first; t <= win[1]; t += step) {
        const x = Math.round(xOf(t)) + 0.5;
        const major = Math.abs(t % (step * 5)) < 1e-6;
        pctx!.strokeStyle = major ? TH.inkDim : TH.grid;
        pctx!.globalAlpha = major ? 0.5 : 1;
        pctx!.beginPath();
        pctx!.moveTo(x, ROW_HEAD);
        pctx!.lineTo(x, PH);
        pctx!.stroke();
        pctx!.globalAlpha = 1;
        if (major || step >= 0.5) {
          pctx!.fillStyle = TH.inkDim;
          pctx!.textAlign = "left";
          const lab = (t < 0 ? "−" : t > 0 ? "+" : "") + Math.abs(t).toFixed(step < 0.2 ? 2 : 1).replace(".", decimalSep());
          pctx!.fillText(lab, x + 4, ROW_HEAD / 2);
        }
      }

      // Störlinie T0
      const x0 = Math.round(xOf(0)) + 0.5;
      if (x0 > -20 && x0 < PW + 20) {
        pctx!.save();
        pctx!.setLineDash([4, 3]);
        pctx!.strokeStyle = TH.rot;
        pctx!.globalAlpha = 0.8;
        pctx!.lineWidth = 1;
        pctx!.beginPath();
        pctx!.moveTo(x0, ROW_HEAD);
        pctx!.lineTo(x0, PH);
        pctx!.stroke();
        pctx!.restore();
      }

      // Abweichungsband (Gutlauf-Diff)
      if (compareL && data.diff) {
        const a = xOf(data.diff.from);
        const b = xOf(data.diff.to);
        pctx!.save();
        pctx!.fillStyle = TH.amber;
        pctx!.globalAlpha = 0.16;
        pctx!.fillRect(a, ROW_HEAD, Math.max(b - a, 4), PH - ROW_HEAD);
        pctx!.globalAlpha = 0.6;
        pctx!.strokeStyle = TH.amber;
        pctx!.setLineDash([3, 2]);
        pctx!.beginPath();
        pctx!.moveTo(a + 0.5, ROW_HEAD);
        pctx!.lineTo(a + 0.5, PH);
        pctx!.moveTo(b + 0.5, ROW_HEAD);
        pctx!.lineTo(b + 0.5, PH);
        pctx!.stroke();
        pctx!.restore();

        const dr = rows.find((r) => r.kind === "sig" && r.ref.id === data.diff!.sig);
        if (dr && b > 0 && a < PW - 40) {
          const ly = dr.y + 4;
          pctx!.fillStyle = TH.amber;
          pctx!.font = `600 10px ${FMONO}`;
          pctx!.textBaseline = "top";
          pctx!.textAlign = "left";
          const tx = Math.max(6, Math.min(b + 7, PW - 92));
          pctx!.fillText(data.diff!.label ?? "", tx, ly);
          pctx!.strokeStyle = TH.amber;
          pctx!.lineWidth = 1;
          pctx!.beginPath();
          pctx!.moveTo(a, ly + 5);
          pctx!.lineTo(Math.max(b, a + 4), ly + 5);
          pctx!.moveTo(a + 0.5, ly + 2);
          pctx!.lineTo(a + 0.5, ly + 8);
          pctx!.moveTo(Math.max(b, a + 4) + 0.5, ly + 2);
          pctx!.lineTo(Math.max(b, a + 4) + 0.5, ly + 8);
          pctx!.stroke();
        }
      }

      // Gerätespuren + Ereignisse
      for (const r of rows) {
        if (r.kind !== "dev") continue;
        const midY = r.y + r.h / 2;
        pctx!.strokeStyle = TH.grid;
        pctx!.beginPath();
        pctx!.moveTo(0, midY + 0.5);
        pctx!.lineTo(PW, midY + 0.5);
        pctx!.stroke();

        for (const e of data.events) {
          if (e.dev !== r.ref.id) continue;
          const x = xOf(e.t);
          if (x < -30 || x > PW + 30) continue;
          const col = lvlCol(e.lvl);
          const sel = Math.abs(e.t - cursor) < 0.02;
          pctx!.fillStyle = col;
          if (e.lvl === "err") {
            pctx!.beginPath();
            pctx!.moveTo(x, midY - 8);
            pctx!.lineTo(x + 7, midY);
            pctx!.lineTo(x, midY + 8);
            pctx!.lineTo(x - 7, midY);
            pctx!.closePath();
            pctx!.fill();
          } else {
            pctx!.beginPath();
            pctx!.arc(x, midY, 5, 0, Math.PI * 2);
            pctx!.fill();
          }
          if (sel) {
            pctx!.strokeStyle = TH.ink;
            pctx!.lineWidth = 1.5;
            pctx!.beginPath();
            pctx!.arc(x, midY, 10, 0, Math.PI * 2);
            pctx!.stroke();
          }
          if (e.payload) {
            pctx!.fillStyle = TH.di;
            pctx!.fillRect(x + 9, midY - 4, 8, 8);
            pctx!.fillStyle = TH.screen;
            pctx!.fillRect(x + 11, midY - 2, 4, 4);
          }
        }
      }

      // Signalspuren
      for (const r of rows) {
        if (r.kind !== "sig") continue;
        const s = r.ref;
        const hi = r.y + 6;
        const lo = r.y + r.h - 8;
        if (compareL && s.ghost) drawTrace(s.ghost, hi, lo, TH.ghost, 1, [3, 3]);
        drawTrace(s.edges, hi, lo, s.type === "DI" ? TH.di : TH.do, 1.8, null);
      }

      // Programm-Spur: Schritt-Segmente (Arbeitsschritte über die Zeit)
      const codeRow = rows.find((r) => r.kind === "code");
      if (codeRow) {
        for (const seg of codeSegs) {
          const x0 = Math.max(0, xOf(seg.t0));
          const x1 = Math.min(PW, xOf(seg.t1));
          if (x1 <= x0) continue;
          const named = !!seg.step;
          pctx!.fillStyle = named ? TH.blau : TH.head;
          pctx!.globalAlpha = named ? 0.22 : 0.5;
          pctx!.fillRect(x0, codeRow.y + 4, x1 - x0, codeRow.h - 8);
          pctx!.globalAlpha = 1;
          pctx!.strokeStyle = named ? TH.blau : TH.grid;
          pctx!.strokeRect(x0 + 0.5, codeRow.y + 4.5, Math.max(1, x1 - x0 - 1), codeRow.h - 9);
          if (seg.step && x1 - x0 > 22) {
            pctx!.save();
            pctx!.beginPath();
            pctx!.rect(x0, codeRow.y, x1 - x0, codeRow.h);
            pctx!.clip();
            pctx!.fillStyle = TH.ink;
            pctx!.font = `600 10px ${FMONO}`;
            pctx!.textBaseline = "middle";
            pctx!.textAlign = "left";
            pctx!.fillText(seg.step + "()", x0 + 5, codeRow.y + codeRow.h / 2);
            pctx!.restore();
          }
        }
      }

      // Cursor
      const cx = Math.round(xOf(cursor)) + 0.5;
      pctx!.strokeStyle = TH.blau;
      pctx!.lineWidth = 1;
      pctx!.beginPath();
      pctx!.moveTo(cx, 0);
      pctx!.lineTo(cx, PH);
      pctx!.stroke();
      pctx!.fillStyle = TH.blau;
      pctx!.beginPath();
      pctx!.moveTo(cx - 6, 0);
      pctx!.lineTo(cx + 6, 0);
      pctx!.lineTo(cx, 9);
      pctx!.closePath();
      pctx!.fill();
    }

    function computeReadout(at: number): Readout {
      const signals: ReadoutSignal[] = data.signals.map((s) => ({
        id: s.id,
        pin: s.pin,
        name: s.name,
        on: valueAt(s.edges, at) === 1,
        kind: s.type,
        since: t("seit {d}", { d: durStr(sinceAt(s.edges, at)) }),
      }));
      let ev = null as (typeof data.events)[number] | null;
      for (const e of data.events) if (e.t <= at + 1e-9) ev = e;
      const event: ReadoutEvent | null = ev
        ? { lvl: ev.lvl, label: LVL_LABEL[ev.lvl], rel: relStr(ev.t), title: ev.title, det: ev.det, payload: ev.payload }
        : null;
      const diffNote = compareL && data.diff && at >= data.diff.from - 0.4 ? data.diff.text : null;
      let code: ReadoutCode | null = null;
      if (data.code) {
        const line = Math.round(valueAt(data.code.track, at));
        code = { line, step: stepAt(data.code.steps, line), text: data.code.lines[line - 1] ?? "" };
      }
      return { rel: relStr(at), abs: clockStr(data.t0Clock, at), signals, event, diffNote, code };
    }

    // Das Video folgt dem Cursor. Beim Scrubben wird es gesucht; während der
    // Wiedergabe läuft es nativ (siehe tick) und wird nur bei spürbarer Drift
    // nachgezogen. Ein Seek pro Frame würde bei MediaRecorder-WebM (kaum
    // Keyframes, kein Index) jeden vorherigen Seek abbrechen — der Decoder käme
    // nie dazu, ein neues Bild zu zeigen, das Video stünde still.
    function setCursor(t: number) {
      cursor = clamp(t, data.bounds[0], data.bounds[1]);
      drawPlot();
      // Führt das Video gerade die Wiedergabe (videoDrives), wird nicht gesucht:
      // der Cursor ist dann aus video.currentTime abgeleitet, ein Seek würde
      // die native Wiedergabe nur unterbrechen.
      if (video && vdur && !videoDrives()) {
        const vt = cursorToVideo(cursor);
        if (Math.abs(video.currentTime - vt) > 0.02) {
          try {
            video.currentTime = vt;
          } catch {
            /* noch nicht seekbar */
          }
        }
      }
      setReadout(computeReadout(cursor));
    }

    function resize() {
      DPR = Math.min(window.devicePixelRatio || 1, 2);
      PW = wrap!.clientWidth || 800;
      plot!.style.height = PH + "px";
      plot!.width = Math.round(PW * DPR);
      plot!.height = Math.round(PH * DPR);
      pctx!.setTransform(DPR, 0, 0, DPR, 0, 0);
      drawPlot();
    }

    // ── Interaktion ──
    const pointerT = (e: PointerEvent) => {
      const r = plot!.getBoundingClientRect();
      return tOf(clamp(e.clientX - r.left, 0, r.width));
    };
    const snapEvent = (t: number) => {
      let best: number | null = null;
      let bd = 1e9;
      const px = (win[1] - win[0]) / PW;
      for (const e of data.events) {
        const d = Math.abs(e.t - t);
        if (d < bd) {
          bd = d;
          best = e.t;
        }
      }
      return best !== null && bd < px * 10 ? best : t;
    };

    const onPointerDown = (e: PointerEvent) => {
      dragging = true;
      stopPlay();
      plot!.setPointerCapture(e.pointerId);
      plot!.focus();
      setCursor(snapEvent(pointerT(e)));
    };
    const onPointerMove = (e: PointerEvent) => {
      if (dragging) setCursor(pointerT(e));
    };
    const onPointerUp = () => {
      dragging = false;
    };
    const onKeyDown = (e: KeyboardEvent) => {
      const fine = e.shiftKey ? 0.005 : 0.02 * (win[1] - win[0]);
      if (e.key === "ArrowLeft") {
        setCursor(cursor - fine);
        e.preventDefault();
      } else if (e.key === "ArrowRight") {
        setCursor(cursor + fine);
        e.preventDefault();
      } else if (e.key === " ") {
        togglePlay();
        e.preventDefault();
      }
    };

    // Wiedergabe: Cursor gleichmäßig vorschieben (Zeitlupe). Das Video läuft
    // dabei nativ mit einer Abspielrate, die dem Cursor-Tempo entspricht;
    // setCursor zieht es nur bei Drift nach.
    const playSpeed = () => (win[1] - win[0] < 1 ? 0.15 : 0.6);
    function syncVideoRate() {
      if (!video || !vdur) return;
      const rate = clamp(playSpeed(), 0.0625, 16);
      if (Math.abs(video.playbackRate - rate) > 1e-3) {
        try {
          video.playbackRate = rate;
        } catch {
          /* Rate vom Browser nicht unterstützt */
        }
      }
    }
    // Während der Wiedergabe ist das Video die Uhr: der Cursor wird aus
    // video.currentTime abgeleitet. Stockt das Video (Nachladen per Range-
    // Streaming), wartet der Cursor mit — statt davonzulaufen und per Seek
    // nachzuziehen, was bei MediaRecorder-WebM die Wiedergabe jedes Mal
    // abwürgt. Ohne Video, außerhalb der Aufnahme oder wenn play() scheitert,
    // läuft der Cursor zeitgesteuert weiter.
    let videoActive = false;
    const videoDrives = () =>
      playing && videoActive && !!video && !video.paused && !video.ended;
    function startVideo() {
      if (!video || !vdur || !videoCovers(cursor)) return;
      syncVideoRate();
      try {
        video.currentTime = cursorToVideo(cursor);
      } catch {
        /* noch nicht seekbar */
      }
      videoActive = true;
      const p = video.play();
      if (p && typeof p.catch === "function") {
        p.catch(() => {
          // Autoplay-Richtlinie o.ä. → zeitgesteuerter Cursor + Seek-Fallback.
          videoActive = false;
        });
      }
    }
    function pauseVideo() {
      videoActive = false;
      if (!video) return;
      try {
        video.pause();
      } catch {
        /* ignore */
      }
    }
    function tick(now: number) {
      if (!playing) return;
      const dt = Math.min((now - lastFrame) / 1000, 0.05);
      lastFrame = now;
      syncVideoRate();
      // Cursor tritt in die Aufnahme ein → Video (erneut) starten.
      if (video && vdur && !videoActive && videoCovers(cursor)) startVideo();
      let nt: number;
      if (videoDrives()) {
        nt = Math.max(cursor, videoToCursor(video!.currentTime));
      } else {
        if (videoActive) videoActive = false; // Video zu Ende / pausiert
        nt = cursor + dt * playSpeed();
      }
      if (nt >= win[1]) {
        nt = win[1];
        stopPlay();
      }
      setCursor(nt);
      if (playing) raf = requestAnimationFrame(tick);
    }
    function stopPlay() {
      if (!playing) return;
      playing = false;
      pauseVideo();
      setPlayingUi(false);
    }
    function togglePlay() {
      if (playing) {
        stopPlay();
        return;
      }
      playing = true;
      setPlayingUi(true);
      if (cursor >= win[1] - 0.02) setCursor(win[0]);
      lastFrame = performance.now();
      startVideo();
      raf = requestAnimationFrame(tick);
    }

    ctrlRef.current = {
      setZoom(z) {
        setZoomUi(z);
        win = z === 2 ? [cursor + data.windows[2][0], cursor + data.windows[2][1]] : ([...data.windows[z]] as [number, number]);
        drawPlot();
      },
      toggleCompare() {
        compareL = !compareL;
        setCompareUi(compareL);
        if (compareL && data.diff) {
          setToast(t("Gutlauf-Referenz überlagert · Median aus 200 fehlerfreien Zyklen") + "\n" + data.diff.text);
        }
        drawPlot();
        setReadout(computeReadout(cursor));
      },
      togglePlay,
      exportPkg() {
        // Echtes Störungspaket: die zusammengesetzte Störung als JSON zum
        // Download. Das Video wird referenziert (logId + Payload-URL), nicht
        // eingebettet — es liegt gestreamt am Payload-Endpunkt.
        const pkg = {
          incidentId: incidentId ?? null,
          exportedAt: new Date().toISOString(),
          t0Clock: data.t0Clock,
          camMeta: data.camMeta ?? null,
          bounds: data.bounds,
          windows: data.windows,
          devices: data.devices,
          events: data.events,
          signals: data.signals,
          diff: data.diff ?? null,
          camera: data.camera
            ? { ...data.camera, payloadUrl: `/api/logs/${data.camera.logId}/payload` }
            : null,
        };
        const blob = new Blob([JSON.stringify(pkg, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `stoerungspaket-${incidentId ?? "incident"}.json`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        setToast(
          data.camera
            ? t("Störungspaket exportiert · {events} Ereignisse · {signals} Signale · Video referenziert", {
                events: data.events.length,
                signals: data.signals.length,
              })
            : t("Störungspaket exportiert · {events} Ereignisse · {signals} Signale", {
                events: data.events.length,
                signals: data.signals.length,
              }),
        );
      },
    };

    const ro = new ResizeObserver(() => resize());
    ro.observe(wrap);
    plot.addEventListener("pointerdown", onPointerDown);
    plot.addEventListener("pointermove", onPointerMove);
    plot.addEventListener("pointerup", onPointerUp);
    plot.addEventListener("pointercancel", onPointerUp);
    plot.addEventListener("keydown", onKeyDown);

    // Theme-Wechsel (dark-Klasse am <html>) → Palette neu lesen + neu zeichnen.
    const themeObs = new MutationObserver(() => {
      TH = readCanvasTheme();
      drawPlot();
    });
    themeObs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });

    // Videolänge übernehmen und das Video an den Cursor holen. MediaRecorder-
    // WebM (Blackbox-Kamera) liefert oft `duration = Infinity` (keine Dauer im
    // Header) — dann bliebe der Scrubber auf Frame 0 stehen („Standbild"). In dem
    // Fall die Dauer einmalig per Seek an ein großes Ziel erzwingen; der Browser
    // lädt bis zum Ende und meldet die echte Dauer via `durationchange`.
    let durationForced = false;
    const resolveDuration = () => {
      if (!video) return;
      const d = video.duration;
      if (Number.isFinite(d) && d > 0) {
        if (Math.abs(d - vdur) < 0.05) return; // wiederholtes durationchange
        vdur = d;
        if (playing) {
          // Dauer erst während der Wiedergabe bekannt → jetzt nativ starten.
          startVideo();
        } else {
          try {
            video.currentTime = cursorToVideo(cursor);
          } catch {
            /* ignore */
          }
        }
      } else if (!durationForced && video.readyState > 0) {
        durationForced = true;
        try {
          video.currentTime = 1e7;
        } catch {
          /* ignore */
        }
      }
    };
    if (video) {
      video.addEventListener("loadedmetadata", resolveDuration);
      video.addEventListener("durationchange", resolveDuration);
    }

    resize();
    setCursor(-0.84);

    return () => {
      ro.disconnect();
      themeObs.disconnect();
      cancelAnimationFrame(raf);
      if (video) {
        pauseVideo();
        video.removeEventListener("loadedmetadata", resolveDuration);
        video.removeEventListener("durationchange", resolveDuration);
      }
      plot.removeEventListener("pointerdown", onPointerDown);
      plot.removeEventListener("pointermove", onPointerMove);
      plot.removeEventListener("pointerup", onPointerUp);
      plot.removeEventListener("pointercancel", onPointerUp);
      plot.removeEventListener("keydown", onKeyDown);
      ctrlRef.current = null;
    };
  }, [data, rows, totalH, incidentId]);

  // Toast automatisch ausblenden
  useEffect(() => {
    if (!toast) return;
    const id = window.setTimeout(() => setToast(null), 4200);
    return () => window.clearTimeout(id);
  }, [toast]);

  const zoomBtns = [t("Zyklus"), t("± 3 s"), t("± 300 ms")];

  return (
    <div
      ref={rootRef}
      className={
        "flex flex-col overflow-auto bg-card " +
        (isFull ? "fixed inset-0 z-[60] rounded-none" : "rounded-md border border-border")
      }
    >
      {/* Kamera + Auslesung */}
      <div className="grid gap-px bg-border" style={{ gridTemplateColumns: "1.35fr 1fr" }}>
        <section className="bg-card">
          <div className="flex items-center gap-2 border-b border-border bg-muted px-3 py-2">
            <h3 className="text-[11px] font-medium uppercase tracking-[0.15em] text-foreground">
              {t("Payload · Kamera-Replay")}
            </h3>
            <span className="ml-auto font-mono text-[10px] text-muted-foreground">{data.camMeta}</span>
          </div>
          <div className="relative bg-muted" style={{ aspectRatio: "16 / 9" }}>
            {data.camera ? (
              <video
                ref={videoRef}
                src={tokenUrl(`/api/logs/${data.camera.logId}/payload`)}
                muted
                playsInline
                preload="metadata"
                className="absolute inset-0 h-full w-full"
                style={{ objectFit: "contain" }}
                aria-label={t("Kamera-Replay der Zelle zum ausgewählten Zeitpunkt")}
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 px-4 text-center">
                <span className="font-mono text-[11px] text-muted-foreground">{t("Kein Kamera-Replay")}</span>
                <span className="font-mono text-[10px] leading-relaxed text-muted-foreground/70">
                  {t("Für diese Störung hat Blackbox kein Video hochgeladen.")}
                </span>
              </div>
            )}
            {/* OSD-Overlay: Wanduhr + REC + relative Zeit — als Chips, damit sie
                auf hellem wie dunklem Bild lesbar bleiben. */}
            <div className="pointer-events-none absolute inset-0 flex flex-col justify-between p-3">
              <div className="flex items-start justify-between">
                <span className="rounded bg-black/45 px-1.5 py-0.5 font-mono text-white" style={{ fontSize: 14 }}>
                  {readout?.abs ?? ""}
                </span>
                {data.camera ? (
                  <span className="flex items-center gap-1.5 rounded bg-black/45 px-1.5 py-0.5 font-mono text-[11px] text-white">
                    <i style={{ width: 8, height: 8, borderRadius: 999, background: A.rot, display: "inline-block" }} />
                    {t("REC")}
                  </span>
                ) : null}
              </div>
              <div className="flex items-end justify-end">
                <span className="rounded bg-black/45 px-1.5 py-0.5 font-mono text-[11px] text-white">
                  {readout?.rel ?? ""}
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-card">
          <div className="flex items-center gap-2 border-b border-border bg-muted px-3 py-2">
            <h3 className="text-[11px] font-medium uppercase tracking-[0.15em] text-foreground">
              {t("Zustand am Cursor")}
            </h3>
            <span className="ml-auto font-mono text-[10px] text-muted-foreground">{t("Klartext statt Pin-Nummer")}</span>
          </div>
          <div className="p-3">
            <div className="mb-2 flex items-baseline gap-2.5 border-b border-border pb-2">
              <span className="text-2xl font-semibold text-foreground">{readout?.rel ?? "—"}</span>
              <span className="font-mono text-[11px] text-muted-foreground">{readout?.abs ?? ""}</span>
            </div>
            <div className="flex flex-col gap-px">
              {(readout?.signals ?? []).map((s) => (
                <div key={s.id} className="grid items-center gap-2 rounded-sm px-1.5 py-1" style={{ gridTemplateColumns: "1fr auto auto" }}>
                  {/* title = voller Name: bei schmalem Panel truncated der
                      Klartext — der Tooltip hält ihn trotzdem erreichbar. */}
                  <span className="truncate text-[12px] text-foreground" title={`${s.name} · ${s.pin}`}>
                    {s.name} <span className="font-mono text-[9px] text-muted-foreground">{s.pin}</span>
                  </span>
                  {s.on ? (
                    <span
                      className="rounded-sm px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
                      style={{
                        color: s.kind === "DI" ? A.di : A.do,
                        background: (s.kind === "DI" ? A.di : A.do) + "24",
                        border: `1px solid ${(s.kind === "DI" ? A.di : A.do) + "66"}`,
                      }}
                    >
                      {t("An")}
                    </span>
                  ) : (
                    <span className="rounded-sm border border-border bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {t("Aus")}
                    </span>
                  )}
                  <span className="text-right font-mono text-[10px] text-muted-foreground" style={{ minWidth: 56 }}>{s.since}</span>
                </div>
              ))}
            </div>
            {readout?.event ? (
              <div className="mt-3 rounded-r-sm p-2.5" style={{ borderLeft: `3px solid ${lvlColor(readout.event.lvl)}`, background: lvlColor(readout.event.lvl) + "14" }}>
                <span className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: lvlColor(readout.event.lvl) }}>
                  {readout.event.label} · {readout.event.rel}
                </span>
                <h4 className="mb-1 mt-0.5 text-sm font-semibold text-foreground">{readout.event.title}</h4>
                <p className="text-[12px] leading-relaxed text-muted-foreground">{readout.event.det}</p>
                {readout.event.payload ? (
                  <span className="mt-2 inline-flex items-center gap-1.5 rounded-sm border border-border bg-muted px-2 py-1 font-mono text-[10px]" style={{ color: A.di }}>
                    ▣ {t("Payload")} · {readout.event.payload}
                  </span>
                ) : null}
                {readout.diffNote ? (
                  <span className="ml-1.5 mt-2 inline-flex items-center gap-1.5 rounded-sm bg-muted px-2 py-1 font-mono text-[10px]" style={{ border: `1px solid ${A.amber}66`, color: A.amber }}>
                    ⟂ {readout.diffNote}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        </section>
      </div>

      {/* Programm · Code-Mitschnitt (Debugger-Ansicht, folgt dem Cursor) */}
      {data.code ? (
        <div className="border-t border-border bg-card">
          <div className="flex items-center gap-2 border-b border-border bg-muted px-3 py-2">
            <h3 className="text-[11px] font-medium uppercase tracking-[0.15em] text-foreground">
              {t("Programm · src0.lua")}
            </h3>
            <span className="ml-auto font-mono text-[10px] text-muted-foreground">
              {readout?.code?.step ? t("Schritt: {step}()", { step: readout.code.step }) : t("kein Schritt")}
              {readout?.code ? " · " + t("Zeile {n}", { n: readout.code.line }) : ""}
            </span>
          </div>
          <div className="overflow-hidden px-2 py-1.5 font-mono text-[11px] leading-[1.55]">
            <CodeView lines={data.code.lines} active={readout?.code?.line ?? 1} />
          </div>
        </div>
      ) : null}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 border-y border-border bg-muted px-3 py-2">
        <div className="flex overflow-hidden rounded-sm border border-border" role="group" aria-label={t("Zeitfenster")}>
          {zoomBtns.map((label, z) => (
            <button
              key={z}
              type="button"
              aria-pressed={zoom === z}
              onClick={() => ctrlRef.current?.setZoom(z)}
              className={
                "px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider " +
                (zoom === z ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground")
              }
            >
              {label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => ctrlRef.current?.togglePlay()}
          className="rounded-sm border border-border bg-secondary px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground"
        >
          {playing ? t("Pause") : t("Abspielen")}
        </button>
        <button
          type="button"
          aria-pressed={compare}
          onClick={() => ctrlRef.current?.toggleCompare()}
          className={
            "rounded-sm border px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider " +
            (compare
              ? "border-success/50 bg-success/15 text-success"
              : "border-border bg-secondary text-muted-foreground hover:text-foreground")
          }
        >
          {t("Gutlauf-Vergleich")}
        </button>
        <button
          type="button"
          onClick={() => ctrlRef.current?.exportPkg()}
          className="rounded-sm bg-primary px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary-foreground"
        >
          {t("Störungspaket exportieren")}
        </button>
        <span className="ml-auto font-mono text-[10px] text-muted-foreground">
          {t("Ziehen zum Scrubben · ← → nudgen · Leertaste abspielen")}
        </span>
        <button
          type="button"
          onClick={toggleFull}
          aria-pressed={isFull}
          title={isFull ? t("Vollbild verlassen") : t("Vollbild")}
          className="rounded-sm border border-border bg-secondary p-1.5 text-muted-foreground hover:text-foreground"
        >
          {isFull ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
        </button>
      </div>

      {/* Zeitstrahl: Labels + Plot */}
      <div className="flex">
        <div className="border-r border-border bg-muted" style={{ width: LABEL_W, flex: `0 0 ${LABEL_W}px` }}>
          {rows.map((r, i) => {
            if (r.kind === "head") {
              return (
                <div key={i} className="flex items-center bg-secondary px-2.5" style={{ height: r.h }}>
                  <span className="text-[9.5px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">{r.label}</span>
                </div>
              );
            }
            if (r.kind === "code") {
              return (
                <div key={i} className="flex items-center px-2.5" style={{ height: r.h }}>
                  <span className="truncate font-mono text-[11px] text-foreground">
                    {readout?.code?.step ? `▸ ${readout.code.step}()` : t("Ablauf")}
                  </span>
                </div>
              );
            }
            if (r.kind === "dev") {
              return (
                <div key={i} className="flex items-center gap-2 border-b border-border px-2.5" style={{ height: r.h }}>
                  <span style={{ width: 7, height: 7, borderRadius: 1, background: r.ref.color, flex: "0 0 7px" }} />
                  <span className="truncate text-[12px] text-foreground" title={r.ref.name}>{r.ref.name}</span>
                </div>
              );
            }
            return (
              <div key={i} className="flex items-center gap-2 border-b border-border px-2.5" style={{ height: r.h }}>
                <span style={{ width: 7, height: 7, borderRadius: 1, background: r.ref.type === "DI" ? A.di : A.do, flex: "0 0 7px" }} />
                <span className="truncate text-[11.5px] text-muted-foreground" title={`${r.ref.name} · ${r.ref.pin}`}>{r.ref.name}</span>
                <span className="ml-auto font-mono text-[9.5px] text-muted-foreground">{r.ref.pin}</span>
              </div>
            );
          })}
        </div>
        <div ref={plotWrapRef} className="relative min-w-0 flex-1">
          <canvas ref={plotRef} tabIndex={0} className="block w-full outline-none" style={{ touchAction: "none", cursor: "ew-resize" }} role="application" aria-label={t("Zeitstrahl aller Geräte und Signale, mit Pfeiltasten navigierbar")} />
        </div>
      </div>

      {/* Legende */}
      <div className="flex flex-wrap gap-4 border-t border-border bg-muted px-3 py-2">
        {[
          { c: A.di, t: t("Eingang (DI)") },
          { c: A.do, t: t("Ausgang (DO)") },
          { c: dark ? "#5c6b74" : "#9aa7ae", t: t("Gutlauf (Referenz)") },
          { c: A.rot, t: t("Störung") },
          { c: A.amber, t: t("Warnung") },
          { c: A.blau, t: t("Cursor") },
        ].map((l) => (
          <span key={l.t} className="flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground">
            <i style={{ width: 14, height: 2, background: l.c, display: "block" }} />
            {l.t}
          </span>
        ))}
      </div>

      {toast ? (
        <div className="pointer-events-none fixed bottom-6 left-1/2 z-50 -translate-x-1/2 whitespace-pre-line rounded-sm border border-border bg-card px-4 py-3 font-mono text-[11.5px] leading-relaxed text-foreground shadow-xl" style={{ borderLeft: `3px solid ${A.gruen}` }}>
          {toast}
        </div>
      ) : null}
    </div>
  );
}
